/**
 * Replace `{{...}}` placeholders in a backend-provided error string with the
 * user-facing label of the property the error is about.
 *
 * The placeholder content (e.g. `{{CampaignNode}}`) is intentionally ignored
 * — the backend uses its own internal token names that don't necessarily map
 * to the form's schema. The frontend is the source of truth for what label
 * the user sees, so we substitute every placeholder with the same resolved
 * label looked up from the property's `x-entity-label` annotation.
 *
 * If the label can't be resolved (no pointer, no schema match, or the
 * property has no `x-entity-label`), the placeholder collapses to its inner
 * text instead — `{{CampaignNode}}` becomes `CampaignNode`. The `{{ }}`
 * syntax is never shown to users.
 *
 * @example
 *   resolveErrorDetailPlaceholders('{{CampaignNode}} is required', 'Campaign Nodes')
 *     // -> 'Campaign Nodes is required'
 *
 *   resolveErrorDetailPlaceholders('{{CampaignNode}} is required', undefined)
 *     // -> 'CampaignNode is required'
 */
export const resolveErrorDetailPlaceholders = (
  text: string,
  propertyLabel: string | undefined,
): string =>
  text.replace(
    /\{\{([^{}]+)\}\}/g,
    (_match, innerText: string) => propertyLabel ?? innerText.trim(),
  );
